import seoImage from '../assets/fevicon.ico'
import { useEffect } from 'react'

export const SITE_NAME = 'Tuhin Kairi'
export const DEFAULT_TITLE = 'Tuhin Kairi | Full-Stack Developer and AI Engineer'
export const DEFAULT_DESCRIPTION =
    'Portfolio of Tuhin Kairi, a full-stack developer and AI engineer building fast web products, scalable systems, case studies, and production-ready digital experiences.'
export const DEFAULT_KEYWORDS = [
    'Tuhin Kairi',
    'full-stack developer',
    'AI engineer',
    'React developer',
    'Next.js developer',
    'portfolio website',
    'system design',
    'case studies',
    'frontend performance',
    'web developer India',
]

type SeoOptions = {
    title: string
    description: string
    keywords?: string[]
    type?: 'website' | 'article'
    robots?: string
    image?: string
    jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
    let tag = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null

    if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute(attribute, key)
        document.head.appendChild(tag)
    }

    tag.content = content
}

function upsertLink(rel: string, href: string) {
    let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null

    if (!link) {
        link = document.createElement('link')
        link.rel = rel
        document.head.appendChild(link)
    }

    link.href = href
}

function upsertJsonLd(jsonLd: Record<string, unknown> | Record<string, unknown>[]) {
    let script = document.head.querySelector('script[data-seo-jsonld="true"]') as HTMLScriptElement | null

    if (!script) {
        script = document.createElement('script')
        script.type = 'application/ld+json'
        script.dataset.seoJsonld = 'true'
        document.head.appendChild(script)
    }

    script.textContent = JSON.stringify(jsonLd)
}

export function usePageSeo({
    title,
    description,
    keywords = DEFAULT_KEYWORDS,
    type = 'website',
    robots = 'index, follow',
    image = seoImage,
    jsonLd,
}: SeoOptions) {
    useEffect(() => {
        const url = window.location.href
        const origin = window.location.origin
        const absoluteImage = image.startsWith('http') ? image : `${origin}${image}`

        document.title = title

        upsertMeta('name', 'description', description)
        upsertMeta('name', 'keywords', keywords.join(', '))
        upsertMeta('name', 'robots', robots)
        upsertMeta('name', 'author', SITE_NAME)
        upsertMeta('property', 'og:site_name', SITE_NAME)
        upsertMeta('property', 'og:title', title)
        upsertMeta('property', 'og:description', description)
        upsertMeta('property', 'og:type', type)
        upsertMeta('property', 'og:url', url)
        upsertMeta('property', 'og:image', absoluteImage)
        upsertMeta('name', 'twitter:card', 'summary_large_image')
        upsertMeta('name', 'twitter:title', title)
        upsertMeta('name', 'twitter:description', description)
        upsertMeta('name', 'twitter:image', absoluteImage)
        upsertLink('canonical', url)

        if (jsonLd) {
            upsertJsonLd(jsonLd)
        }
    }, [description, image, jsonLd, keywords, robots, title, type])
}
